import { LitElement, html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";
import { until } from "lit/directives/until.js";

import { styles } from "./post-card.css";

import { deletePostMutation } from "../../../apps/backoffice/src/services/post-mutations";
import { t } from "../../../apps/backoffice/src/translations/i18n";
import { NotificationService } from "../notification";
import { getPreviewUrl } from "ui-common/preview-url-helpers";
import "../expand-text/expand-text";

export type PostStatus = "live" | "draft" | "planned";

@customElement("post-card")
export class PostCard extends LitElement {
  static override styles = styles;

  @property({ type: String })
  override id = "";

  @property({ type: String })
  teaser = "";

  @property({ type: String })
  mediaUrl = "";

  @property({ type: String })
  status: PostStatus = "draft";

  @property({ type: String })
  contentTier: "free" | "premium" = "free";

  private handleClick = () => {
    this.dispatchEvent(
      new CustomEvent("post-click", {
        bubbles: true,
        composed: true,
        detail: { postId: this.id },
      }),
    );
  };

  private handleDeleteClick() {
    this.isDeleteConfirmationOpen = true;
  }

  @state()
  isDeleteConfirmationOpen = false;

  @property({ type: Boolean })
  hideDeleteButton = false;

  protected override render() {
    return html`
      <div class="post-card" data-status=${this.status}>
        <div class="post-image" style="background-image: url(${until(getPreviewUrl(this.mediaUrl), "#")})">
          <div class="post-gradient-overlay"></div>
          ${this.contentTier === "premium"
            ? html`<div class="premium-badge">
                <ph-crown-simple weight="fill" size="14"></ph-crown-simple>
                <span>Premium</span>
              </div>`
            : nothing}
        </div>

        ${this.hideDeleteButton
          ? nothing
          : html`
              <dropdown-menu class="menu" placement="bottom-end" @dropdown-item-click=${this.handleDropdownItemClick}>
                <ph-dots-three slot="trigger" size="24" class="menu-dots"></ph-dots-three>
                <dropdown-menu-item action="edit" label=${t("links.menu.edit")}>
                  <ph-pencil-line slot="icon" size="16"></ph-pencil-line>
                </dropdown-menu-item>
                <dropdown-menu-item action="delete" label=${t("links.menu.delete")}>
                  <ph-trash-simple slot="icon" size="16"></ph-trash-simple>
                </dropdown-menu-item>
              </dropdown-menu>
            `}

        <div class="post-content">
          <expand-text class="post-teaser" no-more disabled>${this.teaser}</expand-text>
        </div>
      </div>

      ${this.isDeleteConfirmationOpen
        ? html`
            <wow-confirmation header=${t("posts.deleteDialogHeader")}>
              <div class="unsaved-changes-content">
                <p>${t("posts.deleteConfirmation")} "${this.teaser}"?</p>

                <wow-button class="danger" variant="neutral" size="medium" @click=${this.handleDeleteConfirm}>
                  ${t("posts.deleteButtonText")}
                </wow-button>
                <wow-button variant="neutral" size="medium" @click=${this.handleDeleteCancel}> Cancel </wow-button>
              </div>
            </wow-confirmation>
          `
        : ""}
    `;
  }

  private handleDeleteConfirm = async () => {
    try {
      await deletePostMutation.mutate({ postId: this.id });
      NotificationService.success("Post deleted successfully!");
      this.dispatchEvent(new CustomEvent("post-deleted"));
    } catch (error) {
      NotificationService.error("Failed to delete post. Please try again.");
      console.error("Failed to delete world:", error);
    } finally {
      this.isDeleteConfirmationOpen = false;
      this.remove();
    }
  };

  private handleDeleteCancel = () => {
    this.isDeleteConfirmationOpen = false;
  };

  private handleDropdownItemClick(event: CustomEvent) {
    const { action } = event.detail;

    if (action === "edit") {
      this.handleClick();
    }

    if (action === "delete") {
      this.handleDeleteClick();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "post-card": PostCard;
  }
}
